import type { AppliedCalibration } from "@sts/geometry";
import type { ScanCalibration, ScanCalibrationList } from "../api/types.js";

/**
 * The portal's side of scan calibration: turning the API's list into what
 * `@sts/geometry`'s `applyCalibration` takes, and reading a laser figure the
 * way a surveyor types it. The maths itself is the geometry package's — see
 * `packages/geometry/src/calibration.ts`.
 */

/** The calibrations in force on the capture the page is showing, oldest first. */
export function inForce(list: ScanCalibrationList | null | undefined): readonly ScanCalibration[] {
  if (!list) return [];
  return [...list.calibrations]
    .filter((c) => c.applies)
    .sort((a, b) => a.createdAt.localeCompare(b.createdAt));
}

/** What `applyCalibration` needs of each, with who and when for the marker. */
export function appliedCalibrations(list: ScanCalibrationList | null | undefined): readonly AppliedCalibration[] {
  return inForce(list).map((c) => ({
    id: c.id,
    scope: c.scope,
    factor: c.factor,
    provenance: { byName: c.createdByName, at: c.measuredAt ?? c.createdAt },
  }));
}

export type LaserUnit = "mm" | "m";

export type LaserParse =
  | { readonly ok: true; readonly mm: number }
  | { readonly ok: false; readonly message: string }
  | { readonly ok: "empty" };

/**
 * A laser figure as typed — "4230", "4 230", "4,230" in mm, or "4.23" in m —
 * to whole millimetres, which is what a laser meter shows and what the API
 * takes.
 *
 * The unit is the surveyor's to choose, but a figure that only makes sense in
 * the other unit is refused with a pointer rather than taken literally: 4230
 * "m" and 4.23 "mm" are both slips, and the 2% factor guard would catch them
 * only as a baffling "wrong wall".
 */
export function parseLaserInput(text: string, unit: LaserUnit): LaserParse {
  let raw = text.trim().replace(/\s+/g, "");
  if (raw === "") return { ok: "empty" };
  if (raw.includes(",")) {
    if (!/^\d{1,3}(,\d{3})+(\.\d+)?$/.test(raw)) {
      return { ok: false, message: "Use a full stop for decimals, e.g. 4.23 m or 4230 mm." };
    }
    raw = raw.replace(/,/g, "");
  }
  if (!/^\d+(\.\d+)?$|^\.\d+$/.test(raw)) {
    return { ok: false, message: "Type the laser's reading as a number, e.g. 4230 mm or 4.23 m." };
  }
  const value = Number(raw);
  if (!(value > 0)) return { ok: false, message: "The reading must be more than zero." };
  if (unit === "m" && value >= 100) {
    return { ok: false, message: `${raw} m is not a room — did you mean ${raw} mm? Switch the unit to mm.` };
  }
  if (unit === "mm" && value < 30) {
    return { ok: false, message: `${raw} mm is too short for a wall — did you mean ${raw} m? Switch the unit to m.` };
  }
  const mm = Math.round(unit === "m" ? value * 1000 : value);
  if (mm > 30_000) return { ok: false, message: "A laser reading over 30 m is not a room measurement." };
  return { ok: true, mm };
}

/** "4 230 mm" */
export function formatMm(mm: number): string {
  return `${Math.round(mm).toLocaleString("en-GB").replace(/,/g, " ")} mm`;
}

/** "+0.71%" / "−0.34%" */
export function formatDeviation(factor: number): string {
  const pct = (factor - 1) * 100;
  const sign = pct > 0 ? "+" : pct < 0 ? "−" : "±";
  return `${sign}${Math.abs(pct).toFixed(2)}%`;
}
